/**
 * Runtime crash-watcher voor live-updates (port van NovaDirect).
 *
 * @capgo/capacitor-updater doet zelf al boot-rollback wanneer notifyAppReady()
 * niet binnen appReadyTimeout wordt aangeroepen — dat dekt bundles die direct
 * na laden crashen.
 *
 * Dit module dekt het scenario dat een nieuwe bundle wél boot maar vlak daarna
 * runtime-errors gooit (window.onerror / unhandledrejection). Drempel: ≥3
 * *echte* errors binnen 60s na activate → reset naar de native bundle + lokaal
 * blacklisten zodat dezelfde versie niet opnieuw wordt opgehaald. "Echt" =
 * uncaught script-excepties en niet-netwerk rejections; benigne ruis (offline
 * API-calls die rejecten vlak na de reload, afgebroken requests,
 * resource-load fouten) telt NIET mee, anders rolt een gezonde bundle ten
 * onrechte terug.
 */

import { addToBlacklist, readActiveBundle, recordFailContext } from "./liveUpdateState";

const WINDOW_MS = 60000;
const THRESHOLD = 3;

/**
 * Een rejection is benigne (geen bundle-probleem) als 'ie afkomstig is van een
 * mislukte HTTP/API-call of een afgebroken request. Vlak na een update-reload
 * is het toestel vaak even offline en rejecten lopende axios/fetch-calls; die
 * mogen een gezonde bundle niet terugrollen. Een kapotte bundle gooit
 * daarentegen TypeError/ReferenceError of een module-load-fout, en die tellen
 * we wél.
 */
function isBenignRejection(reason) {
    if (!reason || typeof reason !== "object") return false;
    if (reason.name === "AbortError") return true;
    // axios errors hebben isAxiosError/response/status; fetch-achtige fouten
    // statusCode/status of een response-object. Allemaal HTTP-/netwerkfouten.
    if (reason.isAxiosError === true || reason.name === "AxiosError") return true;
    if (typeof reason.statusCode === "number" || typeof reason.status === "number" || "response" in reason) return true;
    return false;
}

let watcherInstalled = false;

export function installLiveUpdateCrashWatcher() {
    if (typeof window === "undefined" || watcherInstalled) return;
    watcherInstalled = true;

    let errorCount = 0;
    let triggered = false;

    const considerRollback = async () => {
        if (triggered) return;
        const active = readActiveBundle();
        if (!active) return;
        if (Date.now() - active.activatedAt > WINDOW_MS) return;
        if (errorCount < THRESHOLD) return;

        triggered = true;
        addToBlacklist(active.version);
        console.error(`[liveUpdate] crash-watch: ${errorCount} errors in ${WINDOW_MS}ms na activatie van ${active.version} — rollback`);
        // Leg de reden vast vóór de reset; na de reboot leest consumeUpdateOutcome
        // dit terug zodat de telemetrie 'crash_rollback' meldt i.p.v. een
        // generieke capgo-revert.
        recordFailContext(active.version, "crash_rollback", `${errorCount} errors in ${WINDOW_MS}ms`);

        try {
            const { Capacitor } = await import("@capacitor/core");
            if (!Capacitor.isNativePlatform()) return;
            const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
            await CapacitorUpdater.reset(); // terug naar de bundled (native shipped) bundle
        } catch (e) {
            console.error("[liveUpdate] reset failed:", e);
        }
    };

    window.addEventListener("error", (event) => {
        // Resource-load fouten (img/script/link) hebben geen .error en mogen niet
        // meetellen; alleen echte uncaught script-excepties zijn een bundle-signaal.
        if (!event.error) return;
        errorCount += 1;
        considerRollback();
    });
    window.addEventListener("unhandledrejection", (event) => {
        if (isBenignRejection(event.reason)) return;
        errorCount += 1;
        considerRollback();
    });
}
